import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject } from 'rxjs';
import { DynamicFilterConfig, EnumValues, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import { Business, BusinessSearchCommand } from '../../../models/business';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { BusinessRoles } from '../../../models/roles/roles-group';
import { SiteService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-businesses',
  templateUrl: './businesses.component.html',
})
export class BusinessesComponent implements OnDestroy {
  private router = inject(Router);
  private businessProxyService = inject(BusinessProxyService);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private userAuthenticationService = inject(UserAuthenticationService);

  isReadOnly = !this.userAuthenticationService.roles.includes(BusinessRoles.BusinessAdmin);
  canAssignSites = this.userAuthenticationService.roles.includes(BusinessRoles.FinanceSiteAssigner);
  private useAllSitesInSearch = AuthService.hasGlobalRole(BusinessRoles.FinanceSiteAssigner);

  private destroy$ = new Subject<void>();
  private lastSearchCriteria: BusinessSearchCommand = {};

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'business-filters', config: this.filterConfig });
  businesses = signal<Business[]>([]);
  businessTypes = signal<EnumValues>({});
  enumsReady = signal(false);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as BusinessSearchCommand));

    effect(
      () => {
        if (!this.enumService.enumsReady()) return;
        const businessTypesValue = this.enumService.getEnum('BusinessEntityType').values;
        this.businessTypes.set(businessTypesValue);
        this.enumsReady.set(true);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'name',
          label: translate('Name', 'Name'),
          placeholder: translate('Enter_name', 'Enter name'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'code',
          label: translate('Code', 'Code'),
          placeholder: translate('Enter_code', 'Enter code'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'businessTypes',
          label: translate('Business_Type', 'Business Type'),
          placeholder: translate('Select_business_types', 'Select business types'),
          options: this.businessTypes(),
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  onSearch(searchCriteria: BusinessSearchCommand): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();
    this.businessProxyService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder(), false, this.useAllSitesInSearch)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.businesses.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: (error) => {
          console.error('Error searching businesses:', error);
          this.businesses.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: BusinessSearchCommand): void {
    const searchCriteria: BusinessSearchCommand = {
      ...filters,
      businessTypes: filters.businessTypes?.map((type) => Number(type)),
    };
    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field;
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  public redirectToDetails(id?: string): void {
    this.router.navigate(['/business', id ?? 'new']);
  }

  public getBusinessTypes(values: number[]): string {
    return values
      .map((value) => this.businessTypes()[value])
      .filter((name) => name !== undefined)
      .join(', ');
  }

  public getAssignedSiteNames(business: Business): string {
    if (!business.siteIds?.length) return '';
    const sites = SiteService.getSites();
    return business.siteIds
      .map((id) => sites.find((s) => s.id === id)?.name ?? '')
      .filter((n) => n)
      .join(', ');
  }

  getRowData(rowData: unknown): Business {
    return rowData as Business;
  }
}
